/**
 * Parse `/new/$facility` URL params produced by `/api/v1/stay`
 * (`selectedSiteIds`, `minStayLength`). Pure — no React, no DOM, so it
 * unit-tests cleanly even though the component that uses it is JSX.
 *
 * - `selectedSiteIds`: CSV of ints. Each id is kept only if it survives
 *   `Number.isFinite` AND is present in `facilitySiteIds`. Returns `null`
 *   when the param is absent, garbage, or resolves to an empty in-facility
 *   subset — the caller then falls back to its existing default (e.g.
 *   site-type-filtered ids). The authoritative facility-membership check
 *   lives in the action handler (`filterToFacilitySites`); this is a UX
 *   convenience so the form never renders a selection the submit would
 *   reject.
 * - `minStayLength`: integer clamped to `[1, 14]` to match `StayLengthInput`'s
 *   range. Returns `null` when the param is absent or not a finite int.
 *
 * Both params may legitimately be absent (the form has existing defaults)
 * so the return type uses `null` to signal "no pre-seed; use your default".
 */
const INT_TOKEN = /^[0-9]+$/;

export function parseDeepLinkSelectedSiteIds(param, facilitySiteIds) {
  if (!param) return null;
  const facilitySet = new Set(facilitySiteIds);
  // Stricter than parseInt-then-isFinite: rejects mixed tokens like "12abc"
  // outright (matches parsePartySize's regex gate in app/routes/api/v1/stay.js).
  // The action-layer R8 guard is still authoritative; this tightens the UX
  // pre-seed so garbage tokens never mask a real in-facility id by accident.
  const parsed = param
    .split(",")
    .filter((token) => INT_TOKEN.test(token.trim()))
    .map((token) => parseInt(token, 10))
    .filter((id) => facilitySet.has(id));
  // Dedup so a crafted `?selectedSiteIds=5,5,5` pre-seeds [5] in the form.
  // Server-side filterToFacilitySites also dedups authoritatively.
  const unique = [...new Set(parsed)];
  return unique.length > 0 ? unique : null;
}

export function parseDeepLinkMinStayLength(param) {
  if (!param) return null;
  const n = parseInt(param, 10);
  if (!Number.isFinite(n)) return null;
  return Math.max(1, Math.min(14, n));
}
